import { BizestVerdictStatus, DevConsoleOrderTrace, OrderTraceKey, TraceSection } from '@/console/api/types'

/**
 * LOGISPRD-13035 — 주문 Trace 탭의 순수 로직.
 *
 * 구간 행의 컬럼명은 테이블 원본 그대로(`SPR_NM` · `cls_date` 처럼 대소문자가 섞인다)라 구간별로
 * 필드를 하드코딩하지 않고, 이 모듈의 보조 함수로 공통 렌더러 하나가 그린다(인계서).
 */
// 인계서 개정본 — 15구간. 이행 요청 원장(fulfillment_request)은 dispatcher 모듈 소유라 Trace 에서 빠졌다.
const META: Record<string, { label: string; what: string }> = {
  statusHistory: { label: '상태 이력', what: '언제 무엇으로 바뀌었나' },
  deliveryOrderStatus: { label: '출고지시 이력', what: '컷 · 합포 · 송장 · 창고' },
  cutoff: { label: '컷오프 차수', what: '차수와 실행 상태' },
  wmsDeliveryOrder: { label: 'WMS 출고지시', what: '전송 플래그' },
  outboundReport: { label: '출고 리포트', what: '실출고 수량 · 일자' },
  outboundResult: { label: '출고 실적', what: '출고 실적 원장 · 택배사는 송장 구간' },
  transportInvoice: { label: '송장', what: '송장번호 · 택배사' },
  erpOutboundResult: { label: 'ERP 실적 전송', what: 'PISTAT · PICODE 가 연동 결과' },
  bizestOutboundResult: { label: '비제스트 실적 전송', what: '판정은 위 “비제스트 출고실적 전송”' },
  bizestCancelRequest: { label: '비제스트 취소 요청', what: '비제스트에서 들어온 취소' },
  bizestCancelResult: { label: '비제스트 취소 결과', what: '비제스트로 돌려준 결과' },
  pantosOutbound: { label: '판토스 출고', what: '해외 물류 출고 전송' },
  orderflowOutbound: { label: 'SCM 허브 출고', what: '출고 실적 전송' },
  wmsCancelResult: { label: 'WMS 취소 결과', what: '취소 요청 여부 · 시각' },
  replanHistory: { label: '리플래닝 이력', what: '출고 재계획 · 사유는 코드만' },
}

export const TRACE_SECTION_KEYS: string[] = Object.keys(META)

export const TRACE_STAGES: { label: string; span: number }[] = [
  { label: '상태', span: 1 },
  { label: '출고지시', span: 3 },
  { label: '출고 실적', span: 3 },
  { label: '외부 전송 · 취소', span: 6 },
  { label: '기타', span: 2 },
]

export const traceSectionMeta = (key: string): { label: string; what: string } => META[key] ?? { label: key, what: '' }

/** 단계 띠는 서버가 인계서 순서 그대로 15개를 줄 때만 맞는다 — 어긋나면 띠를 숨긴다. */
export const hasExpectedTraceLayout = (sections: TraceSection[]): boolean =>
  sections.length === TRACE_SECTION_KEYS.length && sections.every((s, i) => s.key === TRACE_SECTION_KEYS[i])

export const traceSectionAnchor = (key: string): string => `s-trace-sec-${key}`

export type TraceSectionState = 'rows' | 'empty' | 'error' | 'skipped'

export const traceSectionState = (section: TraceSection): TraceSectionState => {
  if (section.error) return 'error'
  if (section.skippedReason) return 'skipped'
  return section.rows.length > 0 ? 'rows' : 'empty'
}

/** 구간당 200행 상한(서버). 넘었는지는 서버의 `truncated` 로만 안다 — 딱 200행인 구간을 잘렸다고 오해하지 않게. */
export const traceSectionBadge = (section: TraceSection): { label: string; tone: 'good' | 'neutral' | 'crit' | 'skip' } => {
  switch (traceSectionState(section)) {
    case 'rows':
      return { label: section.truncated ? '200행 넘음 — 일부만 표시' : `${section.rows.length}행`, tone: 'good' }
    case 'error':
      return { label: '조회 실패', tone: 'crit' }
    case 'skipped':
      return { label: '조회 생략', tone: 'skip' }
    default:
      return { label: '데이터 없음', tone: 'neutral' }
  }
}

export const isOpenByDefault = (state: TraceSectionState): boolean => state === 'rows' || state === 'error'

export const columnsOf = (rows: Record<string, unknown>[]): string[] => {
  const seen = new Set<string>()
  rows.forEach((row) => Object.keys(row).forEach((k) => seen.add(k)))
  return [...seen]
}

export const formatCell = (value: unknown): string => {
  if (value === null || value === undefined) return 'null'
  if (typeof value === 'object') return JSON.stringify(value)
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- rows 는 JSON 에서 온 값이라 이 분기에는 string/number/boolean 만 온다(object 는 위에서 JSON.stringify)
  return String(value)
}

/** 적재 코드의 기존 결함으로 두 컬럼 값이 뒤바뀌어 저장돼 있다 — 의미를 해석하지 않고 표시만 한다. */
export const isSwappedDateColumn = (table: string, column: string): boolean =>
  table === 'IIF_BIZEST_GI' && ['CLS_DATE', 'ACT_DATE'].includes(column.toUpperCase())

const BIZEST: Record<BizestVerdictStatus, { label: string; tone: 'good' | 'warn' | 'crit' | 'neutral'; why: string }> = {
  SENT: { label: '전송 완료', tone: 'good', why: '비제스트가 받았고 오류 문구가 없어요' },
  RESEND_PENDING: { label: '재전송 대기', tone: 'warn', why: '보냈지만 비제스트가 오류를 돌려줬어요. 배치가 다시 보내요' },
  CANCEL_SKIPPED: { label: '취소로 생략', tone: 'neutral', why: '취소된 주문이라 보내지 않고 닫았어요' },
  NOT_SENT: { label: '미전송', tone: 'crit', why: '실적은 만들어졌지만 아직 보내지 않았어요' },
}

export const bizestVerdictMeta = (status: BizestVerdictStatus): { label: string; tone: 'good' | 'warn' | 'crit' | 'neutral'; why: string } =>
  BIZEST[status] ?? { label: status, tone: 'neutral', why: '' }

export const bizestRemarkOf = (trace: DevConsoleOrderTrace, id: number | null): string | null => {
  if (id === null) return null
  const section = trace.sections.find((s) => s.key === 'bizestOutboundResult')
  // r.id 는 rows 가 JSON 원장에서 그대로 온 값이라 타입상 unknown 이다 — BE 는 숫자 id 를 주지만
  // 타입으로 보장되지 않으므로 비교 전에 좁힌다.
  const remark = section?.rows.find((r) => typeof r.id === 'number' && r.id === id)?.REMARK
  return typeof remark === 'string' && remark ? remark : null
}

export type BizestVerdictFallback = { label: string; tone: 'crit' | 'neutral'; desc: string }

/**
 * LOGISPRD-13035 — 판정 목록(`bizestOutboundResults`)이 비었을 때 보여줄 문구.
 *
 * BE 는 9번(`bizestOutboundResult`) 구간 조회의 `rows` 를 그대로 map 해서 판정 목록을 만든다 —
 * 그 구간 조회가 실패하면 `rows = []` 가 되어 판정 목록도 함께 빈다. 그걸 그대로 "실적 미생성"
 * (업무 사실)으로 보여주면 조회 실패를 실적 없음으로 오해한다. 9번 구간 상태로 갈래를 나눈다.
 */
export const bizestVerdictFallback = (trace: DevConsoleOrderTrace): BizestVerdictFallback => {
  const section = trace.sections.find((s) => s.key === 'bizestOutboundResult')
  // 서버가 15구간을 다 안 줬을 때의 방어 — 이때도 "실적 미생성"으로 단정하지 않는다.
  if (!section) return { label: '판정 불가', tone: 'crit', desc: '9번 구간 정보를 받지 못했어요 — 판정할 수 없어요' }
  const state = traceSectionState(section)
  if (state === 'error') return { label: '조회 실패', tone: 'crit', desc: '구간 조회 실패 — 판정할 수 없어요' }
  if (state === 'skipped') {
    // 이 구간의 requiredKeys 는 REF_NO1S 이고 refNo1 은 이 탭 조회의 필수값이라 실제로는 이
    // 갈래에 도달하지 않는다 — 방어적으로만 남겨 둔다.
    return { label: '조회 생략', tone: 'neutral', desc: section.skippedReason ?? '조회 키를 만들 수 없어 건너뛰었어요' }
  }
  // 'empty' | 'rows' — 조회는 됐고(또는 구간에 행이 있어도) 판정 목록만 빈 경우로, 실제로 실적이
  // 아직 만들어지지 않은 정상 상태다.
  return { label: '실적 미생성', tone: 'neutral', desc: '비제스트용 실적이 아직 만들어지지 않았어요' }
}

export const validateTraceKey = (key: OrderTraceKey): string | null => {
  if (!key.refNo1.trim()) return '원주문번호(refNo1)를 넣어 주세요'
  if (!key.orderOptionNumber.trim()) return '주문 옵션번호를 넣어 주세요'
  return null
}
